// Página de un producto: es a donde apunta el link que se comparte por
// WhatsApp, y lo que ven Google y el bot de WhatsApp gracias al prerender.
//
// NO tiene carrito propio a propósito. El carrito y el modal viven dentro de
// Home.js y no hay contexto global; duplicar addToCartFromDetail (tamaños,
// variantes, cantidades) sería replicar la parte más delicada del flujo de
// compra. El CTA lleva a /?p=<slug>, que abre el modal de siempre.

import React from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import Footer from '../components/sections/Footer';
import Seo from '../components/Seo';
import ShareProductButton from '../components/ShareProductButton';
import useProducts from '../hooks/useProducts';
import { businessInfo } from '../mock';
import { featureLines, productPath, productImagePath, productOgImageUrl } from '../lib/productShare';
import { getBreadcrumbJsonLd, SITE_URL } from '../lib/structuredData';

// La descripción para el <meta>: sin el "Tip de Oro" y recortada a lo que
// Google muestra.
const metaDescription = (product) => {
  const text = String(product.description || '').split('🔥')[0].replace(/\s+/g, ' ').trim();
  if (text.length <= 155) return text;
  const cut = text.slice(0, 154);
  return `${cut.slice(0, cut.lastIndexOf(' ')).replace(/[\s,;:.-]+$/, '')}…`;
};

// El precio "desde" que anuncia el JSON-LD: el más bajo al que se puede comprar.
const lowestPrice = (product, type) => {
  const candidates = [];
  if (type === 'brazo') {
    if (product.availableSlice !== false) candidates.push(product.priceSlice);
    if (product.availableFull !== false) candidates.push(product.priceFull);
  }
  if (type === 'galleta') candidates.push(product.priceUnit);
  if (Array.isArray(product.sizes)) {
    candidates.push(...product.sizes.filter((s) => s.available !== false).map((s) => s.price));
  }
  if (Array.isArray(product.options)) candidates.push(...product.options.map((o) => o.price));
  candidates.push(product.price);

  const valid = candidates.filter((n) => typeof n === 'number' && !Number.isNaN(n) && n > 0);
  return valid.length ? Math.min(...valid) : null;
};

const NotFound = ({ slug }) => (
  <div className="min-h-screen bg-cream flex flex-col">
    <Seo
      title="Producto no disponible | Brazolovers"
      description="Este producto ya no está en el menú de Brazolovers."
      path={productPath(slug)}
      noindex
    />
    <SiteHeader />
    <main className="flex-1 container mx-auto px-4 md:px-6 py-16 text-center">
      <h1 className="text-2xl font-bold" style={{ color: '#2c1810' }}>
        Este producto ya no está disponible
      </h1>
      <p className="mt-3" style={{ color: '#8a6a5e' }}>
        Puede que lo hayamos retirado del menú o que el link esté incompleto.
      </p>
      <Link
        to="/menu"
        className="inline-block mt-6 px-6 py-3 rounded-2xl font-semibold"
        style={{ background: '#2c1810', color: 'white' }}
      >
        Ver el menú completo
      </Link>
    </main>
    <Footer businessInfo={businessInfo} />
  </div>
);

const Producto = () => {
  const { slug } = useParams();
  const { productIndex, loadingProducts } = useProducts();

  // entry.product además de entry: una entrada a medias no debe llegar al render.
  const entry = productIndex[slug];

  if (!entry?.product) {
    // Mientras el API responde no sabemos si existe: no afirmamos que no.
    if (loadingProducts) {
      return (
        <div className="min-h-screen bg-cream flex flex-col">
          <SiteHeader />
          <main className="flex-1 container mx-auto px-4 md:px-6 py-16 text-center" style={{ color: '#8a6a5e' }}>
            Cargando…
          </main>
          <Footer businessInfo={businessInfo} />
        </div>
      );
    }
    return <NotFound slug={slug} />;
  }

  const { product, type } = entry;
  const features = featureLines(product, type);
  const description = String(product.description || '').split('🔥')[0].trim();
  const tip = String(product.description || '').split('🔥')[1]?.trim();
  const price = lowestPrice(product, type);
  const isSoldOut = product.available === false;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      description: metaDescription(product),
      image: productOgImageUrl(product.image),
      url: `${SITE_URL}${productPath(slug)}`,
      brand: { '@type': 'Brand', name: 'Brazolovers' },
      ...(price
        ? {
            offers: {
              '@type': 'Offer',
              price: price.toFixed(2),
              priceCurrency: 'USD',
              url: `${SITE_URL}${productPath(slug)}`,
              availability: isSoldOut
                ? 'https://schema.org/OutOfStock'
                : 'https://schema.org/InStock',
            },
          }
        : {}),
    },
    getBreadcrumbJsonLd([
      { name: 'Inicio', path: '/' },
      { name: 'Menú', path: '/menu/' },
      { name: product.name, path: productPath(slug) },
    ]),
  ];

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <Seo
        title={`${product.name} | Brazolovers`}
        description={metaDescription(product)}
        path={productPath(slug)}
        image={productOgImageUrl(product.image)}
        imageAlt={product.name}
        // El JPEG para scrapers; la página pinta el .webp, que pesa menos.
        // Las fotos no tienen un tamaño uniforme (800x800 la mayoría, pero no
        // todas), así que se borran los hints de la portada en vez de mentir.
        imageWidth={null}
        imageHeight={null}
        imageType="image/jpeg"
        type="product"
        jsonLd={jsonLd}
      />
      <SiteHeader />

      <main className="flex-1 container mx-auto px-4 md:px-6 py-8">
        <nav className="text-sm mb-4" style={{ color: '#8a6a5e' }}>
          <Link to="/menu" style={{ color: '#8a6a5e' }}>Menú</Link>
          <span className="mx-2">›</span>
          <span>{product.name}</span>
        </nav>

        <div className="grid md:grid-cols-2 gap-8 items-start">
          <div style={{ borderRadius: '20px', overflow: 'hidden', background: '#fdf8f3' }}>
            <img
              src={productImagePath(product.image)}
              alt={product.name}
              width="800"
              height="800"
              style={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', display: 'block' }}
              onError={(e) => { e.target.src = '/placeholder.jpg'; }}
            />
          </div>

          <div className="flex flex-col gap-5">
            <div>
              <h1 className="text-3xl font-bold" style={{ color: '#2c1810', lineHeight: 1.25 }}>
                {product.name}
              </h1>
              {product.category && (
                <span
                  className="inline-block mt-3"
                  style={{ background: '#2c1810', color: 'white', borderRadius: '20px', padding: '3px 12px', fontSize: '11px', letterSpacing: '0.5px' }}
                >
                  {product.category}
                </span>
              )}
            </div>

            {description && (
              <p style={{ color: '#8a6a5e', lineHeight: 1.75, margin: 0 }}>{description}</p>
            )}

            {features.length > 0 && (
              <ul className="flex flex-col gap-2" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {features.map((line) => (
                  <li
                    key={line}
                    style={{ background: '#fafaf9', border: '1.5px solid #e8e0d8', borderRadius: '12px', padding: '12px 16px', color: '#2c1810', fontSize: '15px' }}
                  >
                    {line}
                  </li>
                ))}
              </ul>
            )}

            {product.bajoPedido && (
              <div style={{ display: 'flex', gap: '8px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '12px', padding: '10px 14px' }}>
                <span style={{ flexShrink: 0 }}>🕒</span>
                <p style={{ fontSize: '13px', color: '#92400e', margin: 0, lineHeight: 1.5 }}>
                  <strong>Bajo pedido</strong> — solicitar con mínimo 24 horas de anticipación
                </p>
              </div>
            )}

            {tip && (
              <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '12px', padding: '12px 14px' }}>
                <p style={{ fontWeight: 600, color: '#92400e', fontSize: '13px', margin: '0 0 3px' }}>🔥 Tip de Oro</p>
                <p style={{ color: '#b45309', fontSize: '13px', lineHeight: 1.5, margin: 0 }}>{tip}</p>
              </div>
            )}

            {isSoldOut ? (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '12px', padding: '14px 16px', color: '#b91c1c', fontSize: '14px' }}>
                <strong>Agotado por el momento</strong> — vuelve pronto, lo reponemos seguido.
              </div>
            ) : (
              <Link
                to={`/?p=${slug}`}
                style={{ display: 'block', textAlign: 'center', padding: '16px', borderRadius: '14px', fontSize: '15px', fontWeight: 600, letterSpacing: '0.3px', background: '#2c1810', color: 'white', textDecoration: 'none', boxShadow: '0 4px 14px rgba(44,24,16,0.25)' }}
              >
                🛒 Pedir este producto
              </Link>
            )}

            <ShareProductButton product={product} type={type} slug={slug} />
          </div>
        </div>
      </main>

      <Footer businessInfo={businessInfo} />
    </div>
  );
};

export default Producto;
